import React, { useState } from 'react';
import { ProcessedRaceRow } from '../types';
import {
  formatCountdown,
  getLiquiditySymbol,
  getCountdownColor,
} from '../utils';

interface RaceRowProps {
  row: ProcessedRaceRow;
  isEvenRow: boolean;
  sharedRaceKey?: string;
  onSharedRaceHover?: (raceKey: string | null) => void;
  isSharedRaceHovered?: boolean;
  alternatingIndex: number;
}

const RaceRow: React.FC<RaceRowProps> = ({
  row,
  isEvenRow,
  sharedRaceKey,
  onSharedRaceHover,
  isSharedRaceHovered = false,
  alternatingIndex,
}) => {
  const [showLiquidityAmount, setShowLiquidityAmount] = useState(false);

  const {
    marketStartTime,
    raceNumber,
    jockey,
    runner,
    backOdds,
    layOdds,
    combinedBackOdds,
    combinedLayOdds,
    liquidityValue,
    isFirstInSharedRace,
    isSharedRace,
    sharedRaceRowCount,
  } = row;

  const countdownInfo = formatCountdown(marketStartTime);
  const countdownColor = getCountdownColor(marketStartTime);
  const liquidityDisplay = getLiquiditySymbol(liquidityValue);

  // Format runner name to remove number prefix (e.g., "1. Air Control" -> "Air Control")
  const formatRunnerName = (runnerName: string | undefined) => {
    if (!runnerName) return '';
    return runnerName.replace(/^\d+\.\s*/, '');
  };

  // Format jockey name to "First Initial. Last Name" for mobile
  const formatJockeyName = (jockeyName: string | undefined) => {
    if (!jockeyName) return '';

    // Split the name by spaces
    const nameParts = jockeyName.trim().split(' ');

    if (nameParts.length < 2) {
      // If only one name part, return as is but truncated if too long
      return jockeyName.length > 6 ? jockeyName.substring(0, 6) : jockeyName;
    }

    // Take first initial and truncated last name
    const firstInitial = nameParts[0].charAt(0).toUpperCase();
    const lastName = nameParts[nameParts.length - 1];

    // Truncate last name by cutting 2 characters (or more if needed to fit)
    const truncatedLastName =
      lastName.length > 4 ? lastName.substring(0, 4) : lastName;

    return `${firstInitial}. ${truncatedLastName}`;
  };

  const cellClasses =
    'px-3 py-2.5 text-xs sm:text-sm whitespace-nowrap relative text-center font-mono text-text-primary-light dark:text-white';
  const numericCellClasses = `${cellClasses}`;
  const oddsCellClasses = `${numericCellClasses}`;

  // Only render shared cells for the first row in a shared race group
  const shouldRenderSharedCell = !isSharedRace || isFirstInSharedRace;
  const rowSpan = isFirstInSharedRace ? sharedRaceRowCount : undefined;

  // Check if we have valid combined data for mobile display
  const hasCombinedData =
    combinedBackOdds &&
    combinedLayOdds &&
    combinedBackOdds !== '-' &&
    combinedLayOdds !== '-';

  // Handle mouse events for shared races
  const handleMouseEnter = () => {
    if (isSharedRace && sharedRaceKey && onSharedRaceHover) {
      onSharedRaceHover(sharedRaceKey);
    }
  };

  const handleMouseLeave = () => {
    if (isSharedRace && onSharedRaceHover) {
      onSharedRaceHover(null);
    }
  };

  // Add alternating row styling
  const getRowClasses = () => {
    let classes =
      'relative border-b border-border-light dark:border-border-dark';

    // Only apply alternating background if not in a hovered shared race
    if (!(isSharedRace && isSharedRaceHovered) && alternatingIndex % 2 === 1) {
      classes += ' bg-hover-light/50 dark:bg-black';
    }

    // Hover effects
    if (isSharedRace && isSharedRaceHovered) {
      // Override any existing background for shared race hover
      classes += ' !bg-hover-light dark:!bg-black';
    } else if (!isSharedRace) {
      // Normal hover for non-shared races
      classes += ' hover:bg-hover-light dark:hover:bg-black';
    }

    return classes;
  };

  return (
    <tr
      className={getRowClasses()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* START TIME - Shared */}
      {shouldRenderSharedCell && (
        <td className={cellClasses} rowSpan={rowSpan}>
          <span
            style={{ color: countdownColor.color }}
            className={countdownColor.className}
          >
            {countdownInfo.text}
          </span>
        </td>
      )}

      {/* RACE # - Shared, now visible on mobile */}
      {shouldRenderSharedCell && (
        <td className={cellClasses} rowSpan={rowSpan}>
          {raceNumber}
        </td>
      )}

      {/* JOCKEY - Never Shared */}
      <td className={`${cellClasses} min-w-[70px] sm:min-w-[120px]`}>
        {/* Desktop: Full name */}
        <span className='hidden sm:inline'>{jockey}</span>
        {/* Mobile: Abbreviated name with elegant overflow */}
        <div className='sm:hidden relative max-w-[60px] mx-auto'>
          <div className='relative overflow-hidden'>
            <span className='block truncate text-center'>
              {formatJockeyName(jockey)}
            </span>
            {/* Fade effect for long names */}
            <div className='absolute top-0 right-0 w-3 h-full bg-gradient-to-l from-surface-light dark:from-surface-dark to-transparent pointer-events-none opacity-80'></div>
          </div>
        </div>
      </td>

      {/* RUNNER - Never Shared, Hidden on mobile */}
      <td
        className={`${cellClasses} font-medium text-text-primary-light dark:text-white min-w-[150px] sm:min-w-[200px] hidden sm:table-cell`}
      >
        {formatRunnerName(runner)}
      </td>

      {/* BACK ODDS - Never Shared, Hidden on mobile */}
      <td className={`${oddsCellClasses} hidden sm:table-cell`}>{backOdds}</td>

      {/* LAY ODDS - Never Shared, Hidden on mobile */}
      <td className={`${oddsCellClasses} hidden sm:table-cell`}>{layOdds}</td>

      {/* COMBINED BACK - Shared, Hidden on mobile */}
      {shouldRenderSharedCell && (
        <td
          className={`${oddsCellClasses} hidden sm:table-cell`}
          rowSpan={rowSpan}
        >
          {combinedBackOdds || '-'}
        </td>
      )}

      {/* COMBINED LAY - Shared, Hidden on mobile */}
      {shouldRenderSharedCell && (
        <td
          className={`${oddsCellClasses} hidden sm:table-cell`}
          rowSpan={rowSpan}
        >
          {combinedLayOdds || '-'}
        </td>
      )}

      {/* COMBINED COMBINED - Mobile only, Shared */}
      {shouldRenderSharedCell && (
        <td className={`${oddsCellClasses} sm:hidden`} rowSpan={rowSpan}>
          {hasCombinedData ? (
            <div className='flex flex-col text-xs'>
              <div>B: {combinedBackOdds}</div>
              <div>L: {combinedLayOdds}</div>
            </div>
          ) : (
            ''
          )}
        </td>
      )}

      {/* LIQUIDITY - Shared, now visible on mobile */}
      {shouldRenderSharedCell && (
        <td className={cellClasses} rowSpan={rowSpan}>
          <div className='relative'>
            <span
              className='font-semibold cursor-pointer hover:opacity-75 transition-opacity'
              style={{ color: '#EDDD53' }}
              onClick={() => setShowLiquidityAmount(!showLiquidityAmount)}
              title={`$${Math.floor(liquidityValue || 0).toLocaleString()}`}
            >
              {liquidityDisplay.symbol}
            </span>
            {showLiquidityAmount && (
              <div className='absolute z-10 bg-black text-white px-2 py-1 rounded text-xs whitespace-nowrap -top-8 left-1/2 transform -translate-x-1/2'>
                ${Math.floor(liquidityValue || 0).toLocaleString()}
              </div>
            )}
          </div>
        </td>
      )}
    </tr>
  );
};

export default RaceRow;
